import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { calculateBridgePrice } from '../data/mockData';
import { loadRatingOptions } from '../data/mockData';
import { QuoteRequestForm } from './QuoteRequestForm';
import { Calculator, PaperPlaneTilt as Mail, TrendUp as TrendingUp, CurrencyDollarSimple as DollarSign } from 'phosphor-react';
import { formatArea, formatLength, formatPerimeter } from '../utils/units';

export const PriceCalculator = ({ configuration, onRequestQuote, showCTA = true, units = 'imperial' }) => {
  const pricing = calculateBridgePrice(configuration);
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const lengthLabel = formatLength(configuration.length, units);
  const widthLabel = formatLength(configuration.width, units);
  const areaLabel = formatArea(configuration.length, configuration.width, units);
  const perimeterLabel = formatPerimeter(pricing.railingPerimeter || configuration.length * 2, units);

  const priceBreakdown = [
    {
      label: 'Base Structure',
      amount: pricing.baseCost,
      description: `${configuration.material} ${configuration.trussType} × ${lengthLabel} × ${widthLabel}`
    },
    {
      label: 'Railing System',
      amount: pricing.railingCost,
      description: `${configuration.railing} railing × ${perimeterLabel} perimeter`
    },
    {
      label: 'Decking',
      amount: pricing.deckingCost,
      description: `${configuration.decking} surface × ${areaLabel}`
    }
  ];

  if (pricing.addOnsCost > 0) {
    priceBreakdown.push({
      label: 'Add-Ons',
      amount: pricing.addOnsCost,
      description: `${configuration.addOns.length} selected features`
    });
  }

  return (
    <Card className="border-slate-600 bg-slate-800 shadow-xl">
      <CardHeader className="pb-3">
        <CardTitle className="text-slate-100 flex items-center gap-2">
          <Calculator className="w-5 h-5 text-green-400" />
          Price Estimate (CAD)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {/* Price Range Display */}
        <div className="bg-gradient-to-r from-green-900/20 to-blue-900/20 border border-green-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-green-400 font-medium">Estimated Price Range</span>
          </div>
          <div className="text-2xl font-bold text-slate-100">
            {formatCurrency(pricing.priceRange.low)} - {formatCurrency(pricing.priceRange.high)}
          </div>
          <div className="text-slate-400 text-sm mt-1">
            For {lengthLabel} × {widthLabel} {configuration.material} {configuration.trussType} bridge{configuration?.subModifiers?.loadRating ? ` • Load: ${(() => {const opt = (loadRatingOptions || []).find?.(o => o.id === configuration.subModifiers.loadRating);return opt?.name || ''})()}` : ''}
          </div>
        </div>

        {/* Price Breakdown */}
        <div className="space-y-3">
          <h4 className="text-slate-200 font-medium">Cost Breakdown:</h4>
          {priceBreakdown.map((item, index) => (
            <div key={index} className="flex justify-between items-start">
              <div className="flex-1">
                <div className="text-slate-300 text-sm font-medium">{item.label}</div>
                <div className="text-slate-500 text-xs">{item.description}</div>
              </div>
              <div className="text-slate-200 font-medium">
                {formatCurrency(item.amount)}
              </div>
            </div>
          ))}

          <Separator className="bg-slate-600" />
          <div className="flex justify-between items-center">
            <div>
              <div className="text-slate-300 font-medium">Subtotal</div>
              <div className="text-slate-500 text-xs">Materials & components</div>
            </div>
            <div className="text-slate-200 font-medium">
              {formatCurrency(pricing.subtotal)}
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <div className="text-slate-300 font-medium">Engineering & Installation</div>
              <div className="text-slate-500 text-xs">25-30% of materials cost</div>
            </div>
            <div className="text-slate-200 font-medium">
              {formatCurrency(pricing.engineeringAndInstallation)}
            </div>
          </div>

          <Separator className="bg-slate-600" />
          <div className="flex justify-between items-center text-lg">
            <div className="text-slate-200 font-semibold">Estimated Total</div>
            <div className="text-green-400 font-bold">
              {formatCurrency(pricing.total)}
            </div>
          </div>

          <div className="text-[10px] text-slate-500">Load factor applied: ×{(pricing.loadMult || 1).toFixed(2)}</div>
        </div>

        {/* Disclaimers */}
        <div className="bg-slate-700 rounded-lg p-3 text-xs text-slate-400">
          <div className="font-medium text-slate-300 mb-1">Important Notes:</div>
          <div className="space-y-1">
            <div>• Prices are rough estimates for planning purposes</div>
            <div>• Final costs depend on site conditions, local codes, and specifications</div>
            <div>• Does not include permits, site preparation, or access roads</div>
            <div>• Engineering analysis may affect final design and pricing</div>
          </div>
        </div>

        {/* Inline CTA optionally shown elsewhere */}
        {showCTA && (
          <QuoteRequestForm configuration={configuration} pricing={pricing}>
            <Button 
              className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-bold py-5 md:py-6 text-lg md:text-xl rounded-xl shadow-2xl ring-1 ring-green-400/30"
            >
              <Mail className="w-6 h-6 mr-3" />
              Get Your Finalized Quote Today!
            </Button>
          </QuoteRequestForm>
        )}

        {/* Trust Indicators */}
        <div className="flex justify-center gap-4 text-xs text-slate-500">
          <Badge variant="outline" className="border-slate-600 text-slate-400 bg-transparent">
            <DollarSign className="w-3 h-3 mr-1" />
            No Hidden Fees
          </Badge>
          <Badge variant="outline" className="border-slate-600 text-slate-400 bg-transparent">
            Free Consultation
          </Badge>
        </div>
        
      </CardContent>
    </Card>
  );
};